/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { RouterModule } from '@angular/router';
import { EditorModule } from '@tinymce/tinymce-angular';
import { LucideCalendarDays, LucideClock } from '@lucide/angular';
import { AppMaterialModule } from 'app/core/app-material/app-material.module';
import { BookingConfirmationDesignFormComponent } from 'app/modules/design/booking-confirmation-design/booking-confirmation-design-management/booking-confirmation-design-form/booking-confirmation-design-form.component';
import { eTicketDesignPreviewViewComponent } from 'app/modules/design/e-ticket-design/e-ticket-design-detail/e-ticket-design-preview-view/e-ticket-design-preview-view.component';
import { ETicketDesignFormComponent } from 'app/modules/design/e-ticket-design/e-ticket-design-management/e-ticket-design-form/e-ticket-design-form.component';
import { TicketShopDesignFormComponent } from 'app/modules/design/ticket-shop-design/ticket-shop-design-management/ticket-shop-design-form/ticket-shop-design-form.component';
import { IconActionButtonListComponent } from 'app/shared/components/icon-action-button-list/icon-action-button-list.component';
import { IconActionButtonComponent } from 'app/shared/components/icon-action-button/icon-action-button.component';
import { ListFilterInlineComponent } from 'app/shared/components/list-filter/list-filter-inline.component';
import { ListFilterComponent } from 'app/shared/components/list-filter/list-filter.component';
import { AsideLayoutComponent } from './components/aside-layout/aside-layout.component';
import { OrbitAsideBodyDirective, OrbitAsideFooterDirective, OrbitAsideHeaderDirective } from './components/aside-layout/aside-layout-slot.directive';
import { FilterFrameworkComponent } from './components/filter-framework/filter-framework.component';
import { OrbitTableComponent } from './components/orbit-table/orbit-table.component';
import { OrbitIconComponent } from './components/orbit-icon/orbit-icon.component';
import { OrbitTableCellTemplateDirective, OrbitTableHeaderTemplateDirective } from './components/orbit-table/orbit-table-template.directive';
import { OrbitStickyTableDirective } from './components/orbit-table/orbit-sticky-table.directive';
import { UploadComponent } from './components/upload/upload.component';
import { PageTitleComponent } from 'app/shared/components/page-title/page-title.component';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import { CookieService, CookieService as KaleidaCookieService } from 'app/shared/services/cookie.service';
import { LogDataService } from 'app/shared/services/log.data.service';
import { LoggingService } from 'app/shared/services/log.service';
import { BlockUIModule } from 'ng-block-ui';
import { FileUploadModule } from 'ng2-file-upload';
import { AutoCompleteAddListComponent } from './components/auto-complete-add-list/auto-complete-add-list.component';
import { BooleanRadioGroupComponent } from './components/boolean-radio-group/boolean-radio-group.component';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from './components/breadcrumb/breadcrumb.service';
import { CollapsibleFormRegionComponent } from './components/collapsible-form-region/collapsible-form-region.component';
import { ColourPickerComponent } from './components/colour-picker/colour-picker.component';
import { ConcurrencyErrorComponent } from './components/concurrency-error/concurrency-error.component';
import { ConfirmationModalComponent } from './components/confirmation-modal/confirmation-modal.component';
import { DatetimePickerComponent } from './components/date-time-picker/date-time-picker.component';
import { InternalFileUploaderComponent } from './components/file-uploader/file-uploader.component';
import { FormattedContentFormComponent } from './components/formatted-content-form/formatted-content-form.component';
import { HtmlContentComponent } from './components/html-content/html-content.component';
import { ImageSelectorComponent } from './components/image-selector/image-selector.component';
import { ColumnHeaderWithCurrencyComponent } from './components/label-with-currency-inner-html/column-header-with-currency.component';
import { InputWithCurrencyPrefixComponent } from './components/label-with-currency-inner-html/input-with-currency-prefix.component';
import { LabelWithCurrencyInnerHtmlComponent } from './components/label-with-currency-inner-html/label-with-currency-inner-html.component';
import { ETicketLayoutPickerComponent } from './components/layout-picker/e-ticket-layout-picker.component';
import { LayoutPickerComponent } from './components/layout-picker/layout-picker.component';
import { TicketShopLayoutPickerComponent } from './components/layout-picker/ticket-shop-layout-picker.component';
import { ListColumnWithTooltipComponent } from './components/list-column-with-tooltip/list-column-with-tooltip.component';
import { ListFilterIntermediaryService } from './components/list-filter/list-filter-intermediary-service.component';
import { PreventEnterDirective } from './components/list-filter/prevent-multi-select-enter.directive';
import { ListPageJumperComponent } from './components/list-page-jumper/list-page-jumper.component';
import { MasterChildViewComponent } from './components/master-child/master-child-view.component';
import { MasterChildComponent } from './components/master-child/master-child.component';
import { MultiSelectChecklistComponent } from './components/multi-select-checklist/multi-select-checklist.component';
import { NoResultsComponent } from './components/no-results/no-results.component';
import { KaleidaPopoverComponent } from './components/pop-over/pop-over.component';
import { KaleidaPopoverDirective } from './components/pop-over/pop-over.directive';
import { PseudoFormFieldErrorComponent } from './components/pseudo-form-field-error/pseudo-form-field-error.component';
import { SimpleFileUploadComponent } from './components/simple-file-upload/simple-file-upload.component';
import { SnackBarComponent } from './components/snack-bar/snack-bar.component';
import { SocialMediaFormListComponent } from './components/social-media-form-list/social-media-form-list.component';
import { TicketPriceTypeAddListComponent } from './components/ticket-price-type-add-list/ticket-price-type-add-list.component';
import { ToBeImplementedComponent } from './components/to-be-implemented/to-be-implemented.component';
import { WarningBoxComponent } from './components/warning-box/warning-box.component';
import { YesNoCancelModalComponent } from './components/yes-no-cancel-modal/yes-no-cancel-modal.component';
import { UppercaseInputDirective } from './directives/format-inputs-uppercase.directive';
import { FormatNumberInputDirective } from './directives/format-number-inputs.directive';
import { TwoDigitDecimalNumberDirective } from './directives/two-digit-decimal-number.directive';
import { TreeNodeHelperService } from './helpers/tree-node-helper.service';
import { ActiveInactivePipe } from './pipes/active-inactive.pipe';
import { BuildDeployedPipe } from './pipes/build-deployed.pipe';
import { EmailLinkPipe } from './pipes/email-link.pipe';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { TimeDisplayPipe } from './pipes/time-display.pipe';
import { UrlLinkPipe } from './pipes/url-link.pipe';
import { YesNoLowercasePipe } from './pipes/yes-no-lowercase.pipe';
import { YesNoPipe } from './pipes/yes-no.pipe';
import { ApplicationService } from './services/application.service';
import { KeepAliveService } from './services/keep-alive.service';
import { SnackBarService } from './services/snack-bar.service';
import { UiStateService } from './services/ui-state.service';

import { BookingConfirmationDesignPreviewViewComponent } from 'app/modules/design/booking-confirmation-design/booking-confirmation-design-detail/booking-confirmation-design-preview-view/booking-confirmation-design-preview-view.component';
import { TicketShopDesignPreviewViewComponent } from 'app/modules/design/ticket-shop-design/ticket-shop-design-detail/ticket-shop-design-preview-view/ticket-shop-design-preview-view.component';
import { CustomerDetailsComponent } from 'app/modules/order/order-search/order-search/customer-details/customer-details.component';
import { CustomerFormComponent } from 'app/modules/order/order-search/order-search/customer-details/customer-form/customer-form.component';
import { CustomerViewComponent } from 'app/modules/order/order-search/order-search/customer-details/customer-view/customer-view.component';
import { OrderSearchListFilterComponent } from 'app/modules/order/order-search/order-search/order-search-list-filter/order-search-list-filter.component';
import { OrderSearchListComponent } from 'app/modules/order/order-search/order-search/order-search-list/order-search-list.component';

@NgModule({
	imports: [CommonModule, BlockUIModule, FormsModule, ReactiveFormsModule, RouterModule, FileUploadModule, AppMaterialModule, EditorModule, UploadComponent, OrbitIconComponent, LucideCalendarDays, LucideClock],
	declarations: [
		AsideLayoutComponent,
		OrbitAsideHeaderDirective,
		OrbitAsideBodyDirective,
		OrbitAsideFooterDirective,
		ConfirmationModalComponent,
		YesNoCancelModalComponent,
		AutoCompleteAddListComponent,
		IconActionButtonComponent,
		IconActionButtonListComponent,
		ListFilterComponent,
		ListFilterInlineComponent,
		FilterFrameworkComponent,
		OrbitTableComponent,
		OrbitTableCellTemplateDirective,
		OrbitTableHeaderTemplateDirective,
		OrbitStickyTableDirective,
		PageTitleComponent,
		SnackBarComponent,
		ListPageJumperComponent,
		SimpleFileUploadComponent,
		InternalFileUploaderComponent,
		ActiveInactivePipe,
		YesNoPipe,
		YesNoLowercasePipe,
		EmailLinkPipe,
		UrlLinkPipe,
		SafeHtmlPipe,
		TimeDisplayPipe,
		NoResultsComponent,
		ToBeImplementedComponent,
		KaleidaPopoverComponent,
		KaleidaPopoverDirective,
		BreadcrumbComponent,
		MultiSelectChecklistComponent,
		ConcurrencyErrorComponent,
		WarningBoxComponent,
		FormattedContentFormComponent,
		HtmlContentComponent,
		TwoDigitDecimalNumberDirective,
		UppercaseInputDirective,
		BuildDeployedPipe,
		CollapsibleFormRegionComponent,
		ImageSelectorComponent,
		ListColumnWithTooltipComponent,
		LabelWithCurrencyInnerHtmlComponent,
		InputWithCurrencyPrefixComponent,
		ColumnHeaderWithCurrencyComponent,
		BooleanRadioGroupComponent,
		ColourPickerComponent,
		FormatNumberInputDirective,
		SocialMediaFormListComponent,
		ETicketLayoutPickerComponent,
		TicketShopLayoutPickerComponent,
		LayoutPickerComponent,
		PseudoFormFieldErrorComponent,
		PreventEnterDirective,
		DatetimePickerComponent,
		TicketPriceTypeAddListComponent,
		BookingConfirmationDesignFormComponent,
		ETicketDesignFormComponent,
		TicketShopDesignFormComponent,
		eTicketDesignPreviewViewComponent,
		BookingConfirmationDesignPreviewViewComponent,
		TicketShopDesignPreviewViewComponent,
		MasterChildComponent,
		MasterChildViewComponent,
		CustomerDetailsComponent,
		CustomerViewComponent,
		CustomerFormComponent,
		OrderSearchListFilterComponent,
		OrderSearchListComponent,
	],
	exports: [
		AsideLayoutComponent,
		OrbitAsideHeaderDirective,
		OrbitAsideBodyDirective,
		OrbitAsideFooterDirective,
		BlockUIModule,
		ConfirmationModalComponent,
		YesNoCancelModalComponent,
		AutoCompleteAddListComponent,
		IconActionButtonComponent,
		IconActionButtonListComponent,
		ListFilterComponent,
		ListFilterInlineComponent,
		FilterFrameworkComponent,
		OrbitTableComponent,
		OrbitIconComponent,
		OrbitTableCellTemplateDirective,
		OrbitTableHeaderTemplateDirective,
		OrbitStickyTableDirective,
		UploadComponent,
		LucideCalendarDays,
		LucideClock,
		PageTitleComponent,
		SnackBarComponent,
		ListPageJumperComponent,
		SimpleFileUploadComponent,
		InternalFileUploaderComponent,
		ActiveInactivePipe,
		BuildDeployedPipe,
		YesNoPipe,
		YesNoLowercasePipe,
		EmailLinkPipe,
		UrlLinkPipe,
		SafeHtmlPipe,
		TimeDisplayPipe,
		NoResultsComponent,
		ToBeImplementedComponent,
		KaleidaPopoverDirective,
		BreadcrumbComponent,
		MultiSelectChecklistComponent,
		ConcurrencyErrorComponent,
		WarningBoxComponent,
		FormattedContentFormComponent,
		HtmlContentComponent,
		TwoDigitDecimalNumberDirective,
		UppercaseInputDirective,
		CollapsibleFormRegionComponent,
		ImageSelectorComponent,
		ListColumnWithTooltipComponent,
		LabelWithCurrencyInnerHtmlComponent,
		InputWithCurrencyPrefixComponent,
		ColumnHeaderWithCurrencyComponent,
		BooleanRadioGroupComponent,
		ColourPickerComponent,
		FormatNumberInputDirective,
		SocialMediaFormListComponent,
		ETicketLayoutPickerComponent,
		TicketShopLayoutPickerComponent,
		LayoutPickerComponent,
		PseudoFormFieldErrorComponent,
		PreventEnterDirective,
		DatetimePickerComponent,
		TicketPriceTypeAddListComponent,
		BookingConfirmationDesignFormComponent,
		ETicketDesignFormComponent,
		TicketShopDesignFormComponent,
		TicketShopDesignPreviewViewComponent,
		eTicketDesignPreviewViewComponent,
		BookingConfirmationDesignPreviewViewComponent,
		MasterChildComponent,
		MasterChildViewComponent,
		CustomerDetailsComponent,
		CustomerViewComponent,
		CustomerFormComponent,
		OrderSearchListFilterComponent,
		OrderSearchListComponent,
	],
	providers: [
		SnackBarService,
		{
			provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
			useValue: { verticalPosition: 'top', horizontalPosition: 'right' },
		},
		KaleidaCookieService,
		CookieService,
		UiStateService,
		ApplicationService,
		{ provide: 'windowObject', useValue: window },
		{ provide: 'navigatorObject', useValue: navigator },
		ReactiveFormHelperService,
		LoggingService,
		LogDataService,
		BreadcrumbService,
		KeepAliveService,
		TreeNodeHelperService,
		ListFilterIntermediaryService,
	],
})
export class SharedModule {}
